import React, { useEffect, useState, MouseEvent } from 'react';
import { InputMask } from '@react-input/mask';
import {
  IconFileDescription,
  IconMapPin,
  IconPhone,
  IconRoute,
  IconRuler2,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import { BoardStage } from '../../../entities/board';
import { Lead } from '../../../entities/lead';
import {
  buildCreateLeadFormValues,
  buildLeadPayload,
  LeadFormValues,
  validateLeadArrival,
  validateOptionalPhone,
} from '../model/leadForm';
import { PHONE_MASK_OPTIONS } from '../../../shared/lib/phone';
import { CitySelect } from '../../../shared/ui/CitySelect';
import { DateTimeTzInput } from '../../../shared/ui/DateTimeTzInput';
import { FormField } from '../../../shared/ui/FormField';
import { NumberInput } from '../../../shared/ui/NumberInput';
import { TextArea } from '../../../shared/ui/Textarea';
import { TextInput } from '../../../shared/ui/TextInput';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';
import { Select } from '../../../shared/ui/Select';
import { fetchPartnersOptions } from '../../../entities/partner';

interface CreateLeadModalProps {
  isOpen: boolean;
  stages: BoardStage[];
  isAdmin: boolean;
  defaultManager: { id: string; name: string };
  onClose: () => void;
  onCreate: (data: Partial<Lead>) => Promise<void>;
}

const FULL_WIDTH_STYLE: React.CSSProperties = { gridColumn: '1/-1' };

const CreateLeadModal: React.FC<CreateLeadModalProps> = ({
  isOpen,
  stages,
  isAdmin,
  defaultManager,
  onClose,
  onCreate,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [downOnOverlay, setDownOnOverlay] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<LeadFormValues>({
    defaultValues: buildCreateLeadFormValues({
      boardId: stages[0]?.id ?? '',
      userId: defaultManager.id,
      userName: defaultManager.name,
    }),
  });

  useEffect(() => {
    if (!isOpen) return;
    reset(buildCreateLeadFormValues({
      boardId: stages[0]?.id ?? '',
      userId: defaultManager.id,
      userName: defaultManager.name,
    }));
    setIsSaving(false);
  }, [defaultManager.id, defaultManager.name, isOpen, reset, stages]);

  if (!isOpen) return null;

  const sending = watch('sending');

  // ponytail: track mousedown target so a text-selection drag ending on the overlay doesn't close the modal
  const handleOverlayMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    setDownOnOverlay(event.target === event.currentTarget);
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && downOnOverlay && !isSaving) {
      onClose();
    }
  };

  const handleCreate = handleSubmit(async values => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onCreate(buildLeadPayload(values));
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <div className="mo open" onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <div className="cm cm-create">
        <div className="cm-top">
          <div>
            <div className="cm-ti">Создать лида</div>
          </div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button" disabled={isSaving} aria-label="Закрыть">
            <IconX size={18} stroke={2.2} />
          </button>
        </div>
        <div className="cm-body">
          <form className="cm-create-body" onSubmit={handleCreate}>
            <div className="sec">Данные лида</div>
            <div className="inf-grid">
              <FormField className="inf-field" label="ФИО клиента" error={errors.title?.message}>
                <TextInput
                  className="inf-input"
                  controlClassName="control-with-icon"
                  icon={<IconUser size={16} stroke={1.9} />}
                  placeholder="ФИО"
                  {...register('title')}
                />
              </FormField>
              {isAdmin ? (
                <FormField className="inf-field" label="Менеджер">
                  <Controller
                    control={control}
                    name="userId"
                    render={({ field }) => (
                      <UserSearchInput
                        name="lead-manager"
                        value={field.value}
                        selectedName={watch('userName')}
                        onSelect={(id, name) => {
                          field.onChange(id);
                          setValue('userName', name, { shouldDirty: true });
                        }}
                        onClear={() => {
                          field.onChange('');
                          setValue('userName', '', { shouldDirty: true });
                        }}
                        placeholder="Без менеджера"
                      />
                    )}
                  />
                </FormField>
              ) : null}
              <FormField className="inf-field" label="Телефон" error={errors.phone?.message}>
                <Controller
                  control={control}
                  name="phone"
                  rules={{ validate: validateOptionalPhone }}
                  render={({ field }) => (
                    <div className="control-with-icon">
                      <span className="field-icon" aria-hidden="true">
                        <IconPhone size={16} stroke={1.9} />
                      </span>
                      <InputMask
                        className="inf-input"
                        name="lead-phone"
                        mask={PHONE_MASK_OPTIONS.mask}
                        replacement={PHONE_MASK_OPTIONS.replacement}
                        placeholder="+7 (___) ___-__-__"
                        type="tel"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  )}
                />
              </FormField>
              <FormField className="inf-field" label="Возраст" error={errors.age?.message}>
                <NumberInput
                  className="inf-input"
                  controlClassName="control-with-icon"
                  icon={<IconRuler2 size={16} stroke={1.9} />}
                  min="0"
                  {...register('age', {
                    validate: value => value === '' || Number(value) >= 0 || 'Возраст не может быть отрицательным',
                  })}
                />
              </FormField>
              <FormField className="inf-field" label="Город выезда">
                <Controller
                  control={control}
                  name="city"
                  render={({ field }) => (
                    <CitySelect
                      name="lead-city"
                      value={field.value}
                      onChange={field.onChange}
                      icon={<IconMapPin size={16} stroke={1.9} />}
                    />
                  )}
                />
              </FormField>
              <FormField className="inf-field" label="Город назначения">
                <Controller
                  control={control}
                  name="destination"
                  render={({ field }) => (
                    <CitySelect
                      name="lead-destination"
                      value={field.value}
                      onChange={field.onChange}
                      icon={<IconRoute size={16} stroke={1.9} />}
                    />
                  )}
                />
              </FormField>
              <FormField className="inf-field" label="Отправление — местные дата и время">
                <Controller
                  control={control}
                  name="sending"
                  render={({ field }) => (
                    <DateTimeTzInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                  )}
                />
              </FormField>
              <FormField className="inf-field" label="Прибытие — местные дата и время" error={errors.arrival?.message}>
                <Controller
                  control={control}
                  name="arrival"
                  rules={{ validate: value => validateLeadArrival(value, sending) }}
                  render={({ field }) => (
                    <DateTimeTzInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                  )}
                />
              </FormField>
            </div>
            <div className="sec">Источник привлечения</div>
            <div className="inf-grid">
              <FormField className="inf-field" label="Партнер">
                <Controller
                  control={control}
                  name="partnerId"
                  render={({ field }) => {
                    const partnerName = watch('partnerName');
                    return (
                      <Select
                        fetchOptions={fetchPartnersOptions}
                        options={field.value && partnerName ? [{ value: field.value, label: partnerName }] : []}
                        value={field.value ?? ''}
                        onChange={(val, opt) => {
                          field.onChange(val);
                          setValue('partnerName', opt?.label ?? '');
                        }}
                        placeholder="Выберите партнера"
                      />
                    );
                  }}
                />
              </FormField>
              <FormField className="inf-field" label="Источник">
                <TextInput
                  className="inf-input"
                  placeholder="Откуда пришёл клиент"
                  {...register('source')}
                />
              </FormField>
            </div>
            <div className="inf-grid">
              <FormField className="inf-field" label="Примечание" style={FULL_WIDTH_STYLE}>
                <TextArea
                  className="inf-textarea"
                  controlClassName="textarea-with-icon"
                  icon={<IconFileDescription size={16} stroke={1.9} />}
                  placeholder="Добавьте примечание по задаче"
                  {...register('note')}
                />
              </FormField>
            </div>
            <div className="cm-r-actions">
              <button className="btn-can" onClick={onClose} type="button" disabled={isSaving}>
                Отмена
              </button>
              <button
                className="btn-save"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? 'Создание...' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { CreateLeadModal };
